import IconLogoGlobalShapeLg from '@/assets/svg/dog-logo-text.svg?react';
import IconLogoShapeLg from '@/assets/svg/fox-logo-text.svg?react';
import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { useUrlConfigModelStore } from '@/models/url-config/urlConfig';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { gaReport } from '@/shared/util/analytics';
import { Link } from '@remix-run/react';
import { Button, Tooltip } from 'aui';
import { FoxIconActionsExport, FoxIconClone } from 'aui-biz-icons';
import clsx from 'clsx';
import { useState } from 'react';
import CloneModal from '../clone-modal/CloneModal';
import ExportModal from '../export-modal/ExportModal';
import styles from './Footer.module.css';

export type FooterProps = {
  className?: string;
};

export function Footer(props: FooterProps) {
  const { className } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const hideCloneButton = useDocsDataModelStore((x) => x.docsBaseConfig.hideCloneButton);
  const exportable = useDocsDataModelStore((x) => x.docsBaseConfig.exportable);
  const hideLogo = useDocsDataModelStore((x) => x.docsBaseConfig.hideLogo);
  const appName = useEnvConfigModelStore((x) => x.APP_NAME);
  const homeIndexUrl = useUrlConfigModelStore((x) => x.home.index);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);

  const onCloneClick = async () => {
    gaReport(DOCS_SITE_EVENT.BUTTON_CLICK_CLONE_DATA, { page_title: title });
    setCloneModalVisible(true);
  };

  return (
    <ContainerQuery containerSizes={[380]} className="@container/footer">
      <footer
        className={clsx(
          'flex flex-col items-center gap-2 border-t border-t-color-split py-10 @[380px]/footer:flex-row',
          className,
        )}
      >
        {exportable && (
          <div className="flex flex-wrap items-center justify-center text-base font-500 @[380px]/footer:justify-start">
            {!hideCloneButton && (
              <>
                <Tooltip
                  align={{ offset: [0, 5] }}
                  placement="top"
                  title={clientI18n('onlineShare.clone', { appName })}
                >
                  <Button
                    type="text"
                    icon={<FoxIconClone size={16} className="mr-1" />}
                    className="px-3 text-secondary"
                    onClick={onCloneClick}
                  >
                    {clientI18n('clone')}
                  </Button>
                </Tooltip>
              </>
            )}
            <Tooltip
              align={{ offset: [0, 5] }}
              placement="top"
              title={clientI18n('onlineShare.export')}
            >
              <Button
                type="text"
                icon={<FoxIconActionsExport size={16} className="mr-1" />}
                className="px-3 text-secondary"
                onClick={() => setExportModalVisible(true)}
              >
                {clientI18n('export')}
              </Button>
            </Tooltip>
          </div>
        )}
        <div
          className={clsx('flex-1', {
            ['text-right']: exportable,
            ['text-center']: !exportable,
          })}
        >
          {!hideLogo && (
            <Tooltip title={clientI18n('slogan')}>
              <div
                className={clsx(
                  'inline-flex items-center text-base font-400 text-secondary',
                  styles['footer-logo-wrapper'],
                )}
              >
                <Link
                  to={homeIndexUrl}
                  className={styles['footer-logo']}
                  onClick={() => {
                    gaReport(DOCS_SITE_EVENT.FOOTER_GOTO_HOME, { page_title: title });
                  }}
                  aria-label="homepage link"
                >
                  <span className="inline-flex items-center">
                    <span className="mr-[-4px]">{clientI18n('built.with')}</span>
                    {IS_GLOBAL ? (
                      <IconLogoGlobalShapeLg className="h-[18px] w-[80px]" />
                    ) : (
                      <IconLogoShapeLg className="h-[18px] w-[80px]" />
                    )}
                  </span>
                </Link>
              </div>
            </Tooltip>
          )}
        </div>
      </footer>
      <ExportModal visible={exportModalVisible} onCancel={() => setExportModalVisible(false)} />
      <CloneModal visible={cloneModalVisible} onCancel={() => setCloneModalVisible(false)} />
    </ContainerQuery>
  );
}
